import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import type { GetConversationResponseDto } from '../types/conversation';
import { getConversation, listConversations } from './conversations.api';

const THREAD_PAGE_SIZE = 20;

export function useConversations(machineId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.conversations(auth.activeCustomerId, machineId),
    queryFn: () => listConversations(machineId),
    enabled: Boolean(enabled && auth.activeCustomerId && machineId),
  });
}

export function useConversation(conversationId: string, enabled = true) {
  const auth = useAuth();
  const query = useInfiniteQuery({
    queryKey: queryKeys.conversation(auth.activeCustomerId, conversationId),
    queryFn: ({ pageParam }) => getConversation(conversationId, pageParam, THREAD_PAGE_SIZE),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_turn_cursor || undefined,
    enabled: Boolean(enabled && auth.activeCustomerId && conversationId),
  });

  const data = query.data ? flattenConversationPages(query.data.pages) : undefined;
  return {
    ...query,
    data,
    fetchOlder: query.fetchNextPage,
    hasOlder: Boolean(query.hasNextPage),
    isFetchingOlder: query.isFetchingNextPage,
  };
}

function flattenConversationPages(pages: GetConversationResponseDto[]): GetConversationResponseDto {
  const first = pages[0];
  const orderedPages = [...pages].reverse();
  const seen = new Set<string>();
  const turns = orderedPages.flatMap((page) => page.turns).filter((turn) => {
    if (seen.has(turn.turn_id)) return false;
    seen.add(turn.turn_id);
    return true;
  });
  const last = pages[pages.length - 1];
  return {
    ...first,
    turns,
    next_turn_cursor: last?.next_turn_cursor ?? null,
  };
}
